/**
 * Customer Delete Action Handlers
 *
 * Handles customer deletion operations for both single and bulk operations.
 * Uses the customer type 'DELETE' to mark accounts for deletion.
 */

import { useQueryClient } from '@tanstack/react-query';

import { useUpdateCustomerType } from '@/hooks/useCustomerType';
import { CustomerData } from '@/sections/customerManagement/types';

/**
 * Hook for customer deletion operations
 *
 * Supports both single and bulk customer deletions with
 * proper error handling and cache invalidation.
 *
 * Note: Deletion is implemented by setting customer type to 'DELETE'
 * which marks the account for deletion in the backend.
 *
 * @returns Object containing handleDeleteCustomers function
 *
 * @example
 * ```tsx
 * const { handleDeleteCustomers } = useCustomerDeleteHandlers();
 *
 * // Single customer delete
 * await handleDeleteCustomers([customer]);
 *
 * // Bulk customer delete
 * await handleDeleteCustomers(selectedCustomers);
 * ```
 */
export const useCustomerDeleteHandlers = () => {
  const updateCustomerType = useUpdateCustomerType();
  const queryClient = useQueryClient();

  /**
   * Deletes one or more customers
   *
   * Marks customers for deletion by setting their type to 'DELETE'.
   * Executes deletions in parallel using Promise.allSettled to handle
   * partial failures gracefully.
   *
   * @param customers - Array of customers to delete (single or multiple)
   * @throws Error if all operations fail
   */
  const handleDeleteCustomers = async (customers: CustomerData[]) => {
    // Execute all deletions in parallel
    const promises = customers.map(customer =>
      updateCustomerType.mutateAsync({
        email: customer.email,
        customerType: 'DELETE',
      })
    );

    // Use Promise.allSettled to handle partial failures
    const results = await Promise.allSettled(promises);

    // Count successes and failures
    const succeeded = results.filter(r => r.status === 'fulfilled').length;
    const failed = results.filter(r => r.status === 'rejected').length;

    // Invalidate relevant queries if any operations succeeded
    if (succeeded > 0) {
      queryClient.invalidateQueries({
        queryKey: ['my', { resource: 'account' }],
      });
    }

    // Log failures for debugging
    if (failed > 0) {
      const failedResults = results
        .filter((r): r is PromiseRejectedResult => r.status === 'rejected')
        .map((r, i) => ({
          customer: customers[i]?.email,
          error: r.reason,
        }));

      console.error(
        '[useCustomerDeleteHandlers] Failed to delete customers:',
        failedResults
      );
    }

    // Throw error if all operations failed
    if (failed === customers.length) {
      throw new Error('Failed to delete all customers');
    }

    // Return success/failure counts for caller to handle messaging
    return { succeeded, failed };
  };

  return {
    handleDeleteCustomers,
    isPending: updateCustomerType.isPending,
  };
};
